import { createFileRoute } from '@tanstack/react-router';
import { statementDisclaimer } from '@namibiago/shared/copy';
import { useAuth } from '@/lib/auth/authContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import { monthLabel, useStatement } from '@/lib/data/statements';
import { formatEurCents, formatNad } from '@/lib/money/format';
import { BalanceByMethodSummary, StatementBreakdownTable } from '@/lib/statements/StatementBreakdown';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/statements/$period/print')({
  component: StatementPrint,
});

/** A single statement, no chrome (`@media print` in global.css hides the
 * shell) — mirrors `bookings.$reference.print.tsx`'s shape. No `OwnerOnly`
 * wrapper: host_statements RLS already refuses a reception account's read
 * (owner-only SELECT policy, 20260926000003), so an unauthorised visit
 * renders an empty/loading state rather than another screen's data, and
 * this route is unreachable from reception's nav in the first place
 * (BUSINESS_NAV marks `/statements` owner-only). */
function StatementPrint() {
  const { period } = Route.useParams();
  const { supabase } = useAuth();
  const { businessId, memberships } = useProperty();
  const { data: statement, isLoading } = useStatement(supabase, businessId, period);
  const businessName = memberships.find((m) => m.businessId === businessId)?.businessName ?? '';

  if (isLoading) return <PageLoading compact />;
  if (!statement) return <p className="p-lg text-body text-ink-2">No statement found for {monthLabel(period)}.</p>;

  return (
    <div className="p-lg">
      <div className="mb-md flex items-center justify-between">
        <div>
          {/* `period` (the URL param), not `statement.period` — see
              statements.$period.index.tsx's identical comment. */}
          <h1 className="text-h2 font-extrabold text-ink">Statement — {monthLabel(period)}</h1>
          <p className="text-body text-ink-2">{businessName}</p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="print:hidden rounded-button bg-canyon px-md py-xs text-body font-semibold text-white"
        >
          Print
        </button>
      </div>

      <dl className="grid grid-cols-2 gap-y-sm text-body text-ink">
        <dt className="text-ink-2">Bookings paid in-app this month</dt>
        <dd>{statement.bookings_count}</dd>

        <dt className="text-ink-2 font-semibold">Fees guests paid to NamibiaGo</dt>
        <dd className="font-semibold">
          {formatNad(statement.fees_paid_nad)}
          {statement.fees_paid_eur_cents != null ? ` (${formatEurCents(statement.fees_paid_eur_cents)})` : ''}
        </dd>

        {Number(statement.guest_payments_nad ?? 0) > 0 ? (
          <>
            <dt className="text-ink-2">Full payments guests made in-app</dt>
            <dd>{formatNad(statement.guest_payments_nad)}</dd>
          </>
        ) : null}

        <dt className="text-ink-2">Refunded to guests</dt>
        <dd>{formatNad(statement.fees_refunded_nad)}</dd>

        <dt className="text-ink-2 font-semibold">Balances you recorded</dt>
        <dd className="font-semibold">{formatNad(statement.balances_recorded_nad)}</dd>
      </dl>

      <div className="mt-lg flex flex-col gap-md">
        <BalanceByMethodSummary balanceByMethod={statement.balance_by_method ?? {}} />
        <StatementBreakdownTable lines={statement.line_items ?? []} />
      </div>

      {/* NEW-4: one shared source with the PDF (see statement-disclaimer.json). */}
      <p className="mt-lg text-caption text-ink-2" data-testid="statement-disclaimer">
        {statementDisclaimer(statement.guest_payments_nad).join(' ')}
      </p>
    </div>
  );
}
